<template>
  <header
    id="header"
    ref="header"
    :class="{
      'is-center': brandRow === 'center',
      diff: diff,
      'is-loading': isFirstLoad,
      'is-loaded': isHeaderAnimated,
    }"
  >
    <NuxtLink data-link class="header__name" :to="'/'">
      <span class="blinker" v-show="isFirstLoad"></span>
      <span
        class="ff-mono tt-u"
        v-show="isFirstLoad"
        :data-scramble="globals?.site_title[0]"
        >LOADING</span
      >
      <AppLink class="ff-mono tt-u" v-show="!isFirstLoad">
        {{ globals?.site_title[0] }}</AppLink
      >
    </NuxtLink>
    <div
      class="header__label ff-mono tt-u"
      v-show="!$mq.isMobileOrTablet"
      data-scramble="DEVELOPMENT & DESIGN"
    ></div>
    <nav class="header__nav" v-show="!$mq.isMobileOrTablet">
      <ul class="header__nav__list ff-mono tt-u">
        <li>
          <NuxtLink :to="'/'">
            <span
              data-scramble="AWWWARDS JURY"
              data-scramble-menu="Home"
              v-show="isFirstLoad"
            ></span>
            <AppLink
              class="header__link"
              indicator
              :style="{
                opacity: isFirstLoad ? 0 : 1,
                position: isFirstLoad ? 'absolute' : 'relative',
              }"
              >Home</AppLink
            >
          </NuxtLink>
        </li>
        <li>
          <NuxtLink :to="'/works'">
            <span data-scramble-menu="Works" v-show="isFirstLoad"></span>
            <AppLink
              class="header__link"
              indicator
              :style="{
                opacity: isFirstLoad ? 0 : 1,
                position: isFirstLoad ? 'absolute' : 'relative',
              }"
              >Works</AppLink
            >
          </NuxtLink>
        </li>
        <li>
          <button>
            <span data-scramble-menu="About" v-show="isFirstLoad"></span>
            <AppLink
              :class="{ 'is-active': isAboutModalVisible }"
              indicator
              @click="isAboutModalVisible = !isAboutModalVisible"
              class="header__link"
              data-custom-link
              :style="{
                opacity: isFirstLoad ? 0 : 1,
                position: isFirstLoad ? 'absolute' : 'relative',
              }"
            >
              About
            </AppLink>
          </button>
        </li>
      </ul>
    </nav>
    <button class="header__cta tt-u ff-mono" v-show="!$mq.isMobileOrTablet">
      <span class="blinker" v-show="!isFirstLoad"></span>
      <AppLink v-show="!isFirstLoad" @click="isMailConfirmVisible = true; useTrackEvent('contact_click', { link_location: 'header' })">
        GET IN TOUCH
      </AppLink>
      <span data-scramble-menu="Get In Touch" v-show="isFirstLoad"> </span>
      <span v-show="isFirstLoad">
        {{ renderIndex(loadingProgress, 3) }}
      </span>
    </button>
    <span
      class="header__menu tt-u ff-mono"
      data-link
      v-show="isFirstLoad && $mq.isMobileOrTablet"
    >
      {{ renderIndex(loadingProgress, 3) }}
    </span>
    <button
      class="header__menu tt-u ff-mono"
      v-show="$mq.isMobileOrTablet && !isFirstLoad"
    >
      <AppLink data-link @click="isMenuOpen = !isMenuOpen">Menu</AppLink>
    </button>
  </header>
</template>

<script setup>
const { $gsap: gsap, $Flip: Flip, $mq } = useNuxtApp();
const { registerDelayedCall } = useAnimationCleanup();

const diff = ref(false);
const globals = useGlobal();
const isMailConfirmVisible = useMailConfirmModal();
const isMenuOpen = useMenuOpen();
const isAboutModalVisible = useAboutModal();
const route = useRoute();
const loadingProgress = useLoadingProgress();
const isFirstLoad = useFirstLoad();

const isHeaderAnimated = ref(false);

const header = ref(false);
const brandRow = useBrandRow();

let flipState;
let cols = [];

const setupFlip = () => {
  cols = gsap.utils.toArray(
    ['.header__name', '.header__label', '.header__nav', '.header__cta'],
    header.value
  );

  gsap.set(cols, { alignSelf: 'start' });
  flipState = Flip.getState(cols);
};

const performFlip = () => {
  if (flipState) {
    Flip.from(flipState, {
      duration: isFirstLoad.value ? 1 : 0.65,
      stagger: 0.05,
      ease: isFirstLoad.value ? 'smooth' : 'smooth',
      force3D: true,
      simple: true,
      props: 'transform',
    });
  }
};

const performExpandFlip = () => {
  if (flipState) {
    gsap.to('.home__reel', {
      clipPath: 'inset(0%)',
      scale: 1,
      force3D: true,
      ease: 'smooth',
      duration: 1.25,
    });
    Flip.from(flipState, {
      duration: 1.25,
      ease: 'smooth',
      force3D: true,
      simple: true,
      props: 'transform',
    });
  }
};

watch(
  () => brandRow.value,
  () => {
    if ($mq.isMobileOrTablet) return;
    setupFlip();
    nextTick(() => {
      performFlip();
    });
  }
);

watch(
  () => route.path,
  () => {
    const dc = gsap.delayedCall(route.path != '/' ? 0 : 0.25, () => {
      diff.value = route.path != '/';
    });
    registerDelayedCall(dc);
  }
);

watch(
  () => isFirstLoad.value,
  v => {
    if ($mq.isMobileOrTablet) return;
    setupFlip();
    isHeaderAnimated.value = true;
    nextTick(() => {
      performExpandFlip();
    });
  }
);

onMounted(() => {
  diff.value = route.path != '/';
});

onUnmounted(() => {
  // Clear Flip state and DOM references
  flipState = null;
  cols = [];
});
</script>

<style lang="scss" scoped>
#header {
  color: #fff;
  display: flex;
  font-size: fluidMap($fontSizes, 13);
  gap: var(--gutter);
  justify-content: space-between;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  pointer-events: none;
  position: fixed;
  right: 0;
  width: 100vw;
  will-change: transform;
  z-index: layer(header);
  mix-blend-mode: difference;

  &.is-center {
    transform: translateY(47.5vh);
  }

  @include mq(desktop) {
    display: grid;
    grid-template-columns: repeat(var(--columns), 1fr);
    padding: var(--offset);
    width: 60vw;
  }

  &.diff {
    mix-blend-mode: difference;
  }

  a,
  button {
    pointer-events: all;
  }
}

[data-link] {
  @include mqDown(desktop) {
    padding: var(--offset);
  }
}

.header__nav__list {
  display: flex;
  gap: var(--offset);
  width: calc(14ch + (var(--offset) * 2));
}

.header__link {
  position: relative;
  clip-path: inset(0% 0% -50% 0%);

  :deep(.link__indicator) {
    background: currentColor;
    bottom: -0.15em;
    height: 0.1em;
    position: absolute;
    width: 100%;
    pointer-events: none;
    transform: scaleX(0);
    transition: transform 0.25s ease(smooth);
    transform-origin: right;
  }

  body:not(:has([data-custom-link].is-active)):not(:has(#app.is-firstload))
    .router-link-exact-active
    & :deep(.link__indicator),
  &.is-active :deep(.link__indicator) {
    transform: scaleX(1);
    transform-origin: left;
  }
}

.header__name {
  align-items: center;
  display: flex;
  grid-column: 1;
  white-space: nowrap;
}

.header__nav {
  @include mq(desktop) {
    grid-column: 5;

    #header.is-loaded & {
      grid-column: 6;
    }
  }
}

.header__label {
  @include mq(desktop) {
    grid-column: 3 / 5;

    #header.is-loaded & {
      grid-column: 2 / 4;
    }
  }
}

.header__cta {
  justify-self: end;
  display: flex;
  align-items: baseline;
  grid-column: 7;
}

.blinker {
  animation: blink 0.5s linear infinite;
  background: #fff;
  display: flex;
  height: 1.4ch;
  margin-right: 1ch;
  width: 1ch;
}

.is-hidden {
}

a {
  position: relative;
}
</style>
